import { Controller } from "@hotwired/stimulus"

// Documentation API : ce qui lui est propre. Les pages en diapositives (bascule, voisines,
// préchargement) sont le fait du contrôleur générique `continuous-pages`, posé sur le même
// élément (cf. docs/frontend/continuous_pages.md).
//
// Ici : réaction immédiate aux clics du sommaire (squelette de page, cases des filtres),
// langage des exemples de requête, bouton Copier.
export default class extends Controller {
  static targets = ["main", "content", "skeleton", "section"]

  static LANGUAGE_KEY = "depozen:api-docs:language"

  initialize() {
    this.restore = () => this.removePending()
    // L'instantané Turbo de la page qu'on quitte ne doit pas garder le token en clair.
    this.maskToken = () => {
      this.element.querySelectorAll(".api-docs-token.is-revealed").forEach((token) => {
        token.classList.remove("is-revealed")
        const value = token.querySelector(".api-docs-token__value")
        if (value) value.textContent = "•".repeat(20)
      })
    }
  }

  connect() {
    this.restoreLanguage()
    // Le snapshot Turbo est pris sur la page qu'on quitte : sans ce retrait, un retour arrière
    // réafficherait le squelette. Idem si la visite échoue.
    document.addEventListener("turbo:before-cache", this.restore)
    document.addEventListener("turbo:before-cache", this.maskToken)
    document.addEventListener("turbo:fetch-request-error", this.restore)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.restore)
    document.removeEventListener("turbo:before-cache", this.maskToken)
    document.removeEventListener("turbo:fetch-request-error", this.restore)
  }

  get scroller() {
    if (this.hasMainTarget && getComputedStyle(this.mainTarget).overflowY !== "visible") return this.mainTarget
    return this.element
  }

  // Clic sur une page du sommaire : la réponse du serveur prend un temps visible, on peint
  // tout de suite le squelette (le lien, lui, est activé par `continuous-pages#activate`).
  //
  // Déclarée avant `continuous-pages#activate` dans data-action : elle voit l'état d'avant le
  // clic. Un clic sur la page déjà ouverte ne change rien à l'écran, pas de squelette.
  navigate(event) {
    if (this.isModifiedClick(event)) return
    const link = event.currentTarget
    if (link.getAttribute("aria-current") === "page" && new URL(link.href).pathname === window.location.pathname) return

    this.paintSkeleton()
  }

  // Clic sur un filtre : la case change d'état sur-le-champ, la liste des guides se grise et
  // le contenu passe en squelette, le temps que le serveur renvoie la page filtrée.
  filter(event) {
    if (this.isModifiedClick(event)) return
    const item = event.currentTarget
    if (item.getAttribute("role") === "menuitemcheckbox") {
      const checked = item.getAttribute("aria-checked") !== "true"
      item.setAttribute("aria-checked", String(checked))
      item.classList.toggle("is-checked", checked)
    }
    this.sectionTargets.forEach((section) => section.classList.add("is-pending"))
    this.paintSkeleton()
  }

  paintSkeleton() {
    if (!this.hasContentTarget || !this.hasSkeletonTarget) return

    this.removeSkeleton()
    this.contentTarget.hidden = true
    const skeleton = this.skeletonTarget.content.firstElementChild.cloneNode(true)
    skeleton.classList.add("api-docs-skeleton--instant")
    this.contentTarget.after(skeleton)
    this.scroller.scrollTop = 0
  }

  removeSkeleton() {
    this.element.querySelectorAll(".api-docs-skeleton--instant").forEach((node) => node.remove())
    if (this.hasContentTarget) this.contentTarget.hidden = false
  }

  removePending() {
    this.removeSkeleton()
    this.sectionTargets.forEach((section) => section.classList.remove("is-pending"))
  }

  isModifiedClick(event) {
    return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0
  }

  // Langage des exemples de requête : un choix pour toute la doc, gardé d'une visite à
  // l'autre. Porté par le conteneur, le CSS montre le bon panneau, y compris dans les pages
  // que le préchargement ajoute ensuite.
  selectLanguage(event) {
    const language = event.currentTarget.dataset.lang
    this.element.dataset.codeLang = language
    try {
      window.localStorage.setItem(this.constructor.LANGUAGE_KEY, language)
    } catch {
      // Stockage indisponible (navigation privée) : le choix vaut pour la page.
    }
  }

  restoreLanguage() {
    try {
      const language = window.localStorage.getItem(this.constructor.LANGUAGE_KEY)
      if (language) this.element.dataset.codeLang = language
    } catch {
      // Stockage indisponible : on garde le langage par défaut.
    }
  }

  async copy(event) {
    const button = event.currentTarget
    const code = button.closest("[data-copy-scope], .api-docs-example__panel")?.querySelector("code")?.textContent
    if (!code) return

    try {
      await navigator.clipboard.writeText(code)
    } catch {
      return
    }
    const label = button.textContent
    button.textContent = button.dataset.copiedLabel
    clearTimeout(button.copyTimeout)
    button.copyTimeout = setTimeout(() => { button.textContent = label }, 1500)
  }
}
